import { useState, useEffect, useCallback } from 'react';
import { facturasService } from '../services';
import { useDebounce } from './useDebounce';

/**
 * Hook personalizado para manejar operaciones CRUD de facturas/ventas
 */
export const useFacturas = () => {
  const [facturas, setFacturas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    estado: '',
    fechaDesde: '',
    fechaHasta: '',
    clienteId: '',
    vendedorId: ''
  });

  // Aplicar debounce al searchQuery (espera 500ms después de que el usuario deje de escribir)
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  /**
   * Transformar datos de la API al formato esperado
   */
  const transformFacturaFromAPI = useCallback((factura) => ({
    id: factura.id,
    numeroFactura: factura.invoice_number || `F-${factura.id}`,
    clienteNombre: factura.client_name || factura.cliente?.company_name || factura.cliente?.full_name || 'Cliente no especificado',
    clienteId: factura.client_id || factura.cliente?.id,
    fecha: factura.invoice_date || factura.created_at || '',
    fechaVencimiento: factura.due_date || '',
    subtotal: factura.subtotal || 0,
    impuestos: factura.taxes || 0,
    descuento: factura.discount || 0,
    total: factura.total || factura.total_amount || 0,
    estado: factura.status || 'pending',
    metodoPago: factura.payment_method || '',
    vendedor: factura.salesperson || factura.assigned_user || '',
    vendedorId: factura.salesperson_id || factura.assigned_user_id,
    notas: factura.notes || '',
    fechaCreacion: factura.created_at || '',
    fechaActualizacion: factura.updated_at || '',
    items: factura.items || [],
    isPaid: factura.is_paid || false,
    pagos: factura.payments || [],
    montoAbonado: factura.amount_paid || 0,
    saldoPendiente: (factura.total || factura.total_amount || 0) - (factura.amount_paid || 0)
  }), []);

  /**
   * Transformar datos de la UI al formato de la API
   */
  const transformFacturaToAPI = useCallback((factura) => {
    const apiData = {
      client_id: factura.clienteId,
      invoice_date: factura.fecha,
      due_date: factura.fechaVencimiento,
      payment_terms: factura.payment_terms || 30,
      currency: factura.currency || 'COP',
      tax_rate: factura.tax_rate || 19,
      exchange_rate: factura.exchange_rate || 1,
      notes: factura.notas,
      reference: factura.reference || '',
      items: factura.items?.map(item => ({
        product_id: item.productoId || item.product_id,
        description: item.nombreProducto || item.description || item.product_name,
        quantity: item.cantidad || item.quantity,
        unit_price: item.precioUnitario || item.unit_price,
        discount_percentage: item.descuentoPorcentaje || item.discount_percentage || 0,
        tax_rate: item.tax_rate || 19,
        unit: item.unit || 'unidad',
        notes: item.notes || ''
      })) || []
    };

    console.log('Transformando factura a API:', factura);
    console.log('Datos transformados:', apiData);

    return apiData;
  }, []);

  /**
   * Cargar todas las facturas
   */
  const loadFacturas = useCallback(async (params = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const defaultParams = {
        skip: 0,
        limit: 50,
        sort: '-created_at',
        ...params
      };

      // Aplicar filtros si existen
      if (filters.estado) defaultParams.status = filters.estado;
      if (filters.fechaDesde) defaultParams.date_from = filters.fechaDesde;
      if (filters.fechaHasta) defaultParams.date_to = filters.fechaHasta;
      if (filters.clienteId) defaultParams.client_id = filters.clienteId;
      if (filters.vendedorId) defaultParams.salesperson_id = filters.vendedorId;

      const result = await facturasService.getFacturas(defaultParams);

      if (result.success) {
        // Manejar diferentes estructuras de respuesta
        const dataArray = Array.isArray(result.data)
          ? result.data
          : (result.data?.items || result.data?.invoices || []);

        const facturasTransformadas = dataArray.map(transformFacturaFromAPI);
        setFacturas(facturasTransformadas);
        return { success: true, data: facturasTransformadas };
      } else {
        setError(result.message || 'Error al cargar facturas');
        setFacturas([]);
        return { success: false, message: result.message };
      }
    } catch (error) {
      setError('Error de conexión al cargar facturas');
      console.error('Error loading facturas:', error);
      setFacturas([]);
      return { success: false, message: 'Error de conexión' };
    } finally {
      setIsLoading(false);
    }
  }, [transformFacturaFromAPI, filters]);

  /**
   * Buscar facturas
   */
  const searchFacturas = useCallback(async (query) => {
    if (!query.trim()) {
      return loadFacturas();
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = {
        skip: 0,
        limit: 50,
        sort: '-created_at',
        search: query
      };

      const result = await facturasService.getFacturas(params);

      if (result.success) {
        const facturasData = result.data.map(transformFacturaFromAPI);
        setFacturas(facturasData);
        return { success: true, data: facturasData };
      } else {
        setError(result.message || 'Error en la búsqueda');
        setFacturas([]);
        return { success: false, message: result.message };
      }
    } catch (err) {
      setError('Error de conexión en la búsqueda');
      console.error('Error searching facturas:', err);
      setFacturas([]);
      return { success: false, message: 'Error de conexión' };
    } finally {
      setIsLoading(false);
    }
  }, [loadFacturas, transformFacturaFromAPI]);

  /**
   * Obtener factura por ID
   */
  const getFacturaById = useCallback(async (id) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await facturasService.getFacturaById(id);

      if (result.success) {
        const facturaTransformada = transformFacturaFromAPI(result.data);
        return { success: true, data: facturaTransformada };
      } else {
        setError(result.message || 'Error al cargar la factura');
        return { success: false, message: result.message };
      }
    } catch (error) {
      setError('Error de conexión al cargar la factura');
      console.error('Error loading factura:', error);
      return { success: false, message: 'Error de conexión' };
    } finally {
      setIsLoading(false);
    }
  }, [transformFacturaFromAPI]);

  /**
   * Crear nueva factura
   */
  const createFactura = useCallback(async (facturaData) => {
    setIsLoading(true);
    setError(null);

    try {
      const apiData = transformFacturaToAPI(facturaData);
      const result = await facturasService.createFactura(apiData);

      if (result.success) {
        const nuevaFactura = transformFacturaFromAPI(result.data);
        setFacturas(prev => [nuevaFactura, ...prev]);
        return { success: true, data: nuevaFactura };
      } else {
        setError(result.message || 'Error al crear la factura');
        return { success: false, message: result.message };
      }
    } catch (error) {
      setError('Error de conexión al crear la factura');
      console.error('Error creating factura:', error);
      return { success: false, message: 'Error de conexión' };
    } finally {
      setIsLoading(false);
    }
  }, [transformFacturaFromAPI, transformFacturaToAPI]);

  /**
   * Actualizar factura existente
   */
  const updateFactura = useCallback(async (id, facturaData) => {
    setIsLoading(true);
    setError(null);

    try {
      const apiData = transformFacturaToAPI(facturaData);
      const result = await facturasService.updateFactura(id, apiData);

      if (result.success) {
        const facturaActualizada = transformFacturaFromAPI(result.data);
        setFacturas(prev => prev.map(f => f.id === id ? facturaActualizada : f));
        return { success: true, data: facturaActualizada };
      } else {
        setError(result.message || 'Error al actualizar la factura');
        return { success: false, message: result.message };
      }
    } catch (error) {
      setError('Error de conexión al actualizar la factura');
      console.error('Error updating factura:', error);
      return { success: false, message: 'Error de conexión' };
    } finally {
      setIsLoading(false);
    }
  }, [transformFacturaFromAPI, transformFacturaToAPI]);

  /**
   * Eliminar factura
   */
  const deleteFactura = useCallback(async (id) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await facturasService.deleteFactura(id);

      if (result.success) {
        setFacturas(prev => prev.filter(f => f.id !== id));
        return { success: true };
      } else {
        setError(result.message || 'Error al eliminar la factura');
        return { success: false, message: result.message };
      }
    } catch (error) {
      setError('Error de conexión al eliminar la factura');
      console.error('Error deleting factura:', error);
      return { success: false, message: 'Error de conexión' };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Marcar factura como pagada
   */
  const markAsPaid = useCallback(async (id, paymentData = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await facturasService.markFacturaAsPaid(id, paymentData);

      if (result.success) {
        const facturaActualizada = transformFacturaFromAPI(result.data);
        setFacturas(prev => prev.map(f => f.id === id ? facturaActualizada : f));
        return { success: true, data: facturaActualizada };
      } else {
        setError(result.message || 'Error al marcar como pagada');
        return { success: false, message: result.message };
      }
    } catch (error) {
      setError('Error de conexión al actualizar el pago');
      console.error('Error marking as paid:', error);
      return { success: false, message: 'Error de conexión' };
    } finally {
      setIsLoading(false);
    }
  }, [transformFacturaFromAPI]);

  /**
   * Obtener estadísticas de facturas
   */
  const getEstadisticas = useCallback(async (params = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await facturasService.getEstadisticas(params);

      if (result.success) {
        return { success: true, data: result.data };
      } else {
        setError(result.message || 'Error al cargar estadísticas');
        return { success: false, message: result.message };
      }
    } catch (error) {
      setError('Error de conexión al cargar estadísticas');
      console.error('Error loading statistics:', error);
      return { success: false, message: 'Error de conexión' };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Aplicar filtros
   */
  const applyFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  /**
   * Limpiar filtros
   */
  const clearFilters = useCallback(() => {
    setFilters({
      estado: '',
      fechaDesde: '',
      fechaHasta: '',
      clienteId: '',
      vendedorId: ''
    });
  }, []);

  /**
   * Limpiar errores
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Generar e imprimir PDF de factura
   */
  const printFactura = useCallback(async (id) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await facturasService.generatePDF(id);

      if (result.success) {
        // Crear URL del blob y abrirlo en nueva ventana
        const blob = new Blob([result.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');

        // Liberar el URL después de un tiempo
        setTimeout(() => window.URL.revokeObjectURL(url), 100);

        return { success: true };
      } else {
        setError(result.message || 'Error al generar el PDF');
        return { success: false, message: result.message };
      }
    } catch (error) {
      setError('Error de conexión al generar el PDF');
      console.error('Error printing factura:', error);
      return { success: false, message: 'Error de conexión' };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Descargar PDF de factura
   */
  const downloadFactura = useCallback(async (id, numeroFactura) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await facturasService.generatePDF(id);

      if (result.success) {
        // Crear URL del blob y descargarlo
        const blob = new Blob([result.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Factura_${numeroFactura || id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Liberar el URL
        setTimeout(() => window.URL.revokeObjectURL(url), 100);

        return { success: true };
      } else {
        setError(result.message || 'Error al generar el PDF');
        return { success: false, message: result.message };
      }
    } catch (error) {
      setError('Error de conexión al generar el PDF');
      console.error('Error downloading factura:', error);
      return { success: false, message: 'Error de conexión' };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Efecto para búsqueda con debounce
  useEffect(() => {
    if (debouncedSearchQuery !== searchQuery) return;
    searchFacturas(debouncedSearchQuery);
  }, [debouncedSearchQuery, searchFacturas, searchQuery]);

  // Recargar cuando cambien los filtros
  useEffect(() => {
    loadFacturas();
  }, [loadFacturas]);

  return {
    // Estado
    facturas,
    isLoading,
    error,
    searchQuery,
    filters,

    // Acciones
    loadFacturas,
    searchFacturas,
    getFacturaById,
    createFactura,
    updateFactura,
    deleteFactura,
    markAsPaid,
    getEstadisticas,
    printFactura,
    downloadFactura,

    // Filtros y búsqueda
    setSearchQuery,
    applyFilters,
    clearFilters,

    // Utilidades
    clearError,
    transformFacturaFromAPI,
    transformFacturaToAPI
  };
};

export default useFacturas;